import { useEffect, useState } from 'react';
import client from '../api/client';

export default function Commissions() {
  const [stats, setStats] = useState(null);
  const [reps, setReps] = useState([]);
  const [selected, setSelected] = useState(null);
  const [repDetail, setRepDetail] = useState(null);
  const [loading, setLoading] = useState(true);
  const [msg, setMsg] = useState('');
  const [payLoading, setPayLoading] = useState(false);
  const [overrideVal, setOverrideVal] = useState('');
  const [overrideLoading, setOverrideLoading] = useState(false);
  const [overrideMsg, setOverrideMsg] = useState('');
  const [showHelp, setShowHelp] = useState(false);

  useEffect(() => {
    Promise.all([
      client.get('/admin/affiliate/stats'),
      client.get('/admin/affiliate/reps')
    ]).then(([s, r]) => { setStats(s.data); setReps(r.data || []); }).finally(() => setLoading(false));
  }, []);

  const loadRep = async (id) => {
    setSelected(id);
    const r = await client.get('/admin/affiliate/reps/' + id);
    setRepDetail(r.data);
    setOverrideVal(r.data.rep?.commissionOverride != null ? r.data.rep.commissionOverride : '');
    setOverrideMsg('');
  };

  const [payoutModal, setPayoutModal] = useState(null); // {repId, repName, amount, count}
  const [payoutOtp, setPayoutOtp] = useState('');
  const [payoutOtpSent, setPayoutOtpSent] = useState(false);
  const [payoutLoading, setPayoutLoading] = useState(false);

  const requestPayoutOtp = async (repId) => {
    setPayoutLoading(true);
    try {
      const r = await client.post('/admin/affiliate/commissions/' + repId + '/request-payout-otp');
      setPayoutModal({ repId, repName: repDetail.rep?.name, amount: r.data.pendingAmount, count: r.data.pendingCount });
      setPayoutOtpSent(true);
      setPayoutOtp('');
    } catch(e) { alert(e.response?.data || 'Failed to send OTP'); }
    finally { setPayoutLoading(false); }
  };

  const confirmPayout = async () => {
    if (!payoutOtp) return;
    setPayoutLoading(true);
    try {
      await client.post('/admin/affiliate/commissions/' + payoutModal.repId + '/mark-paid', { otp: payoutOtp });
      alert('Commissions released! $' + Number(payoutModal.amount).toFixed(2) + ' paid to ' + payoutModal.repName);
      setPayoutModal(null); setPayoutOtp(''); setPayoutOtpSent(false);
      loadRep(payoutModal.repId);
    } catch(e) { alert(e.response?.data || 'Payout failed'); }
    finally { setPayoutLoading(false); }
  };

  const markPaid = async (repId) => {
    setPayLoading(true);
    try {
      await client.post('/admin/affiliate/commissions/' + repId + '/mark-paid');
      setMsg('Commissions marked as paid');
      loadRep(repId);
    } finally { setPayLoading(false); }
  };

  const saveOverride = async (repId) => {
    setOverrideLoading(true);
    try {
      await client.post('/admin/affiliate/reps/' + repId + '/commission-override', {
        override: overrideVal === '' ? null : parseFloat(overrideVal)
      });
      setOverrideMsg('Override saved successfully');
      loadRep(repId);
    } catch(e) { setOverrideMsg(e.response?.data || 'Failed'); } finally { setOverrideLoading(false); }
  };

  const btn = (c) => ({ padding:'7px 14px', background:c, color:'#000', border:'none', borderRadius:6, cursor:'pointer', fontSize:13, fontWeight:600 });

  return (
    <div>
      <h1 style={{ color:'#000', fontSize:22, fontWeight:700, margin:'0 0 24px', fontFamily:'Georgia,serif' }}>Commissions</h1>

      {msg && <div style={{ background:'#f0fff6', border:'1px solid #b2dfdb', borderRadius:8, padding:'10px 16px', color:'#0a8a40', fontSize:13, marginBottom:16 }}>{msg}</div>}

      {/* Stats */}
      {stats && (
        <div style={{ display:'flex', gap:16, marginBottom:24, flexWrap:'wrap' }}>
          {[
            { label:'Active Reps', value:stats.totalReps, color:'#000' },
            { label:'Pending Applications', value:stats.pendingApplications, color:'#d97706' },
            { label:'Total Referrals', value:stats.totalReferrals, color:'#1a5cff' },
            { label:'Pending Payout', value:'$'+(Number(stats.pendingCommissions)||0).toFixed(2), color:'#e05c5c' },
            { label:'Total Paid Out', value:'$'+(Number(stats.totalCommissionsPaid)||0).toFixed(2), color:'#0a8a40' },
          ].map(s => (
            <div key={s.label} style={{ background:'#fff', borderRadius:12, padding:'20px 24px', border:'1px solid #eee', flex:1, minWidth:160 }}>
              <div style={{ color:'#555', fontSize:11, textTransform:'uppercase', letterSpacing:1, marginBottom:8 }}>{s.label}</div>
              <div style={{ color:s.color, fontSize:24, fontWeight:700 }}>{s.value}</div>
            </div>
          ))}
        </div>
      )}

      <div style={{ display:'flex', gap:16 }}>
        {/* Reps List */}
        <div style={{ background:'#fff', borderRadius:12, border:'1px solid #eee', overflow:'hidden', width:320, flexShrink:0 }}>
          <div style={{ padding:'16px 20px', borderBottom:'1px solid #eee', color:'#000', fontWeight:600 }}>Sales Reps</div>
          {loading ? <div style={{ padding:20, color:'#555' }}>Loading...</div>
          : reps.length === 0 ? <div style={{ padding:20, color:'#555' }}>No reps yet</div>
          : reps.map(r => (
            <div key={r.id} onClick={()=>loadRep(r.id)} style={{ padding:'14px 20px', borderBottom:'1px solid #f5f5f5', cursor:'pointer', background:selected===r.id?'#f9f9f9':'transparent' }}>
              <div style={{ color:'#000', fontSize:14, fontWeight:500 }}>{r.name}</div>
              <div style={{ color:'#555', fontSize:12 }}>{r.refCode}</div>
            </div>
          ))}
        </div>

        {/* Rep Detail */}
        <div style={{ flex:1 }}>
          {!repDetail ? (
            <div style={{ background:'#fff', borderRadius:12, padding:40, border:'1px solid #eee', textAlign:'center' }}>
              <div style={{ color:'#555', fontSize:14 }}>Select a rep to view their commissions</div>
            </div>
          ) : (
            <div style={{ background:'#fff', borderRadius:12, border:'1px solid #eee', overflow:'hidden' }}>
              <div style={{ padding:'16px 20px', borderBottom:'1px solid #eee', display:'flex', justifyContent:'space-between', alignItems:'center' }}>
                <div>
                  <div style={{ color:'#000', fontWeight:600 }}>{repDetail.rep?.name}</div>
                  <div style={{ color:'#555', fontSize:12 }}>Earned: <span style={{ color:'#0a8a40' }}>${Number(repDetail.totalEarned||0).toFixed(2)}</span> | Pending: <span style={{ color:'#e05c5c' }}>${Number(repDetail.pendingPayout||0).toFixed(2)}</span></div>
                </div>
                <div style={{ display:'flex', gap:8, alignItems:'center', flexWrap:'wrap' }}>
                  {Number(repDetail.pendingPayout) > 0 && (
                    <button style={{ padding:'7px 14px', background:'#f0fff6', color:'#0a8a40', border:'none', borderRadius:6, cursor:payoutLoading?'not-allowed':'pointer', fontSize:13, fontWeight:700, opacity:payoutLoading?0.7:1 }} onClick={()=>requestPayoutOtp(repDetail.rep?.id)} disabled={payoutLoading}>{payoutLoading?'Sending OTP...':'Release Payout'}</button>
                  )}
                </div>
              </div>
              <div style={{ padding:'14px 20px', borderBottom:'1px solid #eee', background:'#f9f9f9' }}>
                <div style={{ display:'flex', alignItems:'center', justifyContent:'space-between', marginBottom:8 }}>
                  <div style={{ fontSize:15, fontWeight:800, color:'#000' }}>Commission Override</div>
                  <button onClick={()=>setShowHelp(!showHelp)} style={{ width:26, height:26, borderRadius:'50%', background:'#1a3cff', color:'#fff', border:'none', fontSize:13, fontWeight:800, cursor:'pointer', flexShrink:0 }}>?</button>
                </div>
                <div style={{ fontSize:12, color:'#666', marginBottom:10 }}>Set a custom pool rate for this rep. Leave blank to use global settings (25% default).</div>
                {showHelp && (
                  <div style={{ background:'#eff4ff', borderRadius:10, padding:16, border:'1px solid #c7d9ff', marginBottom:14 }}>
                    <div style={{ fontSize:13, fontWeight:800, color:'#1a3cff', marginBottom:10 }}>How Commission Override Works</div>
                    <div style={{ fontSize:13, color:'#1a3cff', lineHeight:1.7, marginBottom:12 }}>
                      By default all reps use the global commission pool rate. The override lets you give a specific rep a different pool rate.
                    </div>
                    <div style={{ display:'grid', gridTemplateColumns:'1fr 1fr', gap:10, marginBottom:12 }}>
                      <div style={{ background:'#fff', borderRadius:8, padding:12, border:'1px solid #c7d9ff' }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#888', textTransform:'uppercase', marginBottom:6 }}>Without override</div>
                        <div style={{ fontSize:13, color:'#000' }}>Global pool: <b>25%</b></div>
                        <div style={{ fontSize:12, color:'#555', marginTop:4 }}>On $100 payment:</div>
                        <div style={{ fontSize:12, color:'#1a3cff', marginTop:2 }}>Sales Partner earns $15.00</div>
                      </div>
                      <div style={{ background:'#fff', borderRadius:8, padding:12, border:'1px solid #c7d9ff' }}>
                        <div style={{ fontSize:11, fontWeight:700, color:'#888', textTransform:'uppercase', marginBottom:6 }}>With override (30%)</div>
                        <div style={{ fontSize:13, color:'#000' }}>Custom pool: <b>30%</b></div>
                        <div style={{ fontSize:12, color:'#555', marginTop:4 }}>On $100 payment:</div>
                        <div style={{ fontSize:12, color:'#0a8a40', marginTop:2 }}>Sales Partner earns $18.00</div>
                      </div>
                    </div>
                    <div style={{ fontSize:12, fontWeight:700, color:'#1a3cff', marginBottom:6 }}>How to apply:</div>
                    <div style={{ fontSize:12, color:'#1a3cff', lineHeight:1.8 }}>
                      1. Enter the pool % (e.g. 30 for 30%)<br/>
                      2. Click Save Override<br/>
                      3. To remove: clear the field and save — rep returns to global rate<br/>
                      4. Maximum allowed: 35%
                    </div>
                  </div>
                )}
                <div style={{ display:'flex', gap:8, alignItems:'center' }}>
                  <input
                    type="number" min="0" max="35" step="0.5"
                    placeholder="e.g. 30 for 30% pool"
                    value={overrideVal}
                    onChange={e=>setOverrideVal(e.target.value)}
                    style={{ padding:'8px 12px', border:'1px solid #ddd', borderRadius:6, fontSize:13, color:'#000', outline:'none', width:180 }}
                  />
                  <span style={{ color:'#555', fontSize:13 }}>% pool rate</span>
                  <button style={{ padding:'8px 16px', background:'#1a3cff', color:'#fff', border:'none', borderRadius:6, fontSize:13, fontWeight:700, cursor:overrideLoading?'not-allowed':'pointer', opacity:overrideLoading?0.6:1 }}
                    onClick={()=>saveOverride(repDetail.rep?.id)} disabled={overrideLoading}>
                    {overrideLoading?'Saving...':'Save Override'}
                  </button>
                  {overrideVal !== '' && (
                    <button style={{ padding:'8px 12px', background:'#fff5f5', color:'#c0392b', border:'none', borderRadius:6, fontSize:13, fontWeight:700, cursor:'pointer' }}
                      onClick={()=>{ setOverrideVal(''); }}>Clear</button>
                  )}
                </div>
                {overrideMsg && <div style={{ marginTop:8, fontSize:12, color:overrideMsg.includes('success')?'#0a8a40':'#c0392b', fontWeight:600 }}>{overrideMsg}</div>}
                {overrideVal !== '' && (
                  <div style={{ marginTop:8, fontSize:12, color:'#555' }}>
                    At {overrideVal}% pool on $100: SP gets ${(100 * parseFloat(overrideVal||0) * 0.6 / 100).toFixed(2)}, AM gets ${(100 * parseFloat(overrideVal||0) * 0.2 / 100).toFixed(2)}, RM gets ${(100 * parseFloat(overrideVal||0) * 0.2 / 100).toFixed(2)}
                  </div>
                )}
              </div>
              <table style={{ width:'100%', borderCollapse:'collapse' }}>
                <thead><tr style={{ borderBottom:'1px solid #eee' }}>
                  {['Tenant','Amount','Commission','Period','Status','Date'].map(h=>(
                    <th key={h} style={{ color:'#555', fontSize:11, fontWeight:500, textAlign:'left', padding:'12px 16px', textTransform:'uppercase' }}>{h}</th>
                  ))}
                </tr></thead>
                <tbody>
                  {(repDetail.commissions||[]).length === 0
                    ? <tr><td colSpan={6} style={{ color:'#555', padding:24, textAlign:'center' }}>No commissions yet</td></tr>
                    : (repDetail.commissions||[]).map((c,i) => (
                      <tr key={i} style={{ borderBottom:'1px solid #f5f5f5' }}>
                        <td style={{ padding:'10px 16px', color:'#000', fontSize:13 }}>{c.tenant_name||'—'}</td>
                        <td style={{ padding:'10px 16px', color:'#333', fontSize:13 }}>${Number(c.payment_amount).toFixed(2)}</td>
                        <td style={{ padding:'10px 16px', color:'#0a8a40', fontSize:13, fontWeight:600 }}>${Number(c.commission_amount).toFixed(2)}</td>
                        <td style={{ padding:'10px 16px', color:'#333', fontSize:13 }}>{c.period_month}/{c.period_year}</td>
                        <td style={{ padding:'10px 16px' }}><span style={{ background:c.status==='PAID'?'#f0fff6':'#fff8ec', color:c.status==='PAID'?'#0a8a40':'#b45309', padding:'2px 8px', borderRadius:4, fontSize:12, fontWeight:600 }}>{c.status}</span></td>
                        <td style={{ padding:'10px 16px', color:'#555', fontSize:12 }}>{c.created_at?new Date(c.created_at).toLocaleDateString():'—'}</td>
                      </tr>
                    ))
                  }
                </tbody>
              </table>
            </div>
          )}
        </div>
      </div>

      {payoutModal && (
        <div style={{ position:'fixed', inset:0, background:'rgba(0,0,0,0.4)', display:'flex', alignItems:'center', justifyContent:'center', zIndex:1000 }}>
          <div style={{ background:'#fff', borderRadius:12, padding:32, width:460, boxShadow:'0 8px 32px rgba(0,0,0,0.18)' }}>
            <h3 style={{ color:'#0a8a40', margin:'0 0 6px', fontFamily:'Georgia,serif' }}>Release Commission Payout</h3>
            <p style={{ color:'#666', fontSize:13, margin:'0 0 18px' }}>OTP has been sent to your admin email. Enter it below to confirm.</p>
            <div style={{ background:'#f0fff6', borderRadius:8, padding:'12px 16px', marginBottom:16, fontSize:13 }}>
              <strong>{payoutModal.repName}</strong><br/>
              <span style={{ color:'#0a8a40', fontSize:16, fontWeight:700 }}>${Number(payoutModal.amount).toFixed(2)}</span>
              <span style={{ color:'#888', fontSize:12 }}> · {payoutModal.count} commission{payoutModal.count !== 1 ? 's' : ''}</span>
            </div>
            <div style={{ marginBottom:14 }}>
              <label style={{ fontSize:12, fontWeight:700, color:'#555', display:'block', marginBottom:4 }}>OTP CODE *</label>
              <input value={payoutOtp} onChange={e=>setPayoutOtp(e.target.value)} placeholder="Enter 6-digit OTP"
                style={{ width:'100%', padding:'9px 12px', border:'1px solid #ddd', borderRadius:8, fontSize:15, letterSpacing:4, boxSizing:'border-box' }} />
            </div>
            <div style={{ display:'flex', gap:10 }}>
              <button onClick={confirmPayout} disabled={!payoutOtp || payoutLoading}
                style={{ flex:1, padding:'11px', background:'#0a8a40', color:'#fff', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer', opacity:!payoutOtp?0.5:1 }}>
                {payoutLoading ? 'Processing...' : 'Confirm Payout'}
              </button>
              <button onClick={()=>{ setPayoutModal(null); setPayoutOtp(''); setPayoutOtpSent(false); }}
                style={{ flex:1, padding:'11px', background:'#f5f5f5', color:'#444', border:'none', borderRadius:8, fontSize:14, fontWeight:700, cursor:'pointer' }}>
                Cancel
              </button>
            </div>
            <div style={{ marginTop:12, textAlign:'center' }}>
              <button onClick={()=>requestPayoutOtp(payoutModal.repId)} style={{ background:'none', border:'none', color:'#1a3cff', fontSize:12, cursor:'pointer' }}>Resend OTP</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
